/**
 * Offline Queue Service - IndexedDB-based queue for field reliability
 * 
 * Features:
 * - Queues photos and incidents when offline
 * - Auto-syncs when connection restored
 * - Retry logic with exponential backoff
 * - Deduplication and idempotency
 * - Progress tracking and UI state
 */

import Dexie from 'dexie';

// Initialize IndexedDB database
const db = new Dexie('RapidXOfflineQueue');

db.version(1).stores({
  // Photo upload queue
  photoQueue: '++id, incident_id, checkpoint_id, patrol_run_id, status, created_at, retry_count',
  // Incident creation queue
  incidentQueue: '++id, temp_id, site_id, status, created_at, retry_count',
  // General sync queue for other actions
  syncQueue: '++id, type, payload, status, created_at, retry_count',
  // Sync metadata
  syncMeta: 'key'
});

// Queue status constants
export const QUEUE_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  UPLOADED: 'uploaded',
  FAILED: 'failed',
  RETRYING: 'retrying'
};

// Max retry attempts
const MAX_RETRIES = 5;

// Retry delay calculation (exponential backoff)
const getRetryDelay = (retryCount) => Math.min(1000 * Math.pow(2, retryCount), 30000);

/**
 * Add photo to offline queue
 */
export const queuePhoto = async ({
  file,
  incidentId,
  checkpointId,
  patrolRunId,
  caption,
  gpsLat,
  gpsLng
}) => {
  // Convert file to base64 for storage
  const base64 = await fileToBase64(file);
  
  const entry = {
    file_data: base64,
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
    incident_id: incidentId || null,
    checkpoint_id: checkpointId || null,
    patrol_run_id: patrolRunId || null,
    caption: caption || null,
    gps_lat: gpsLat || null,
    gps_lng: gpsLng || null,
    status: QUEUE_STATUS.PENDING,
    created_at: new Date().toISOString(),
    retry_count: 0,
    last_error: null,
    uploaded_url: null
  };
  
  const id = await db.photoQueue.add(entry);
  console.log('[OfflineQueue] Photo queued:', id);
  
  // Try to sync immediately if online
  if (navigator.onLine) {
    syncPhotos();
  }
  
  return id;
};

/**
 * Add incident to offline queue
 */
export const queueIncident = async ({
  title,
  description,
  category,
  severity,
  siteId,
  patrolRunId,
  checkpointId,
  gpsLat,
  gpsLng,
  photos = []
}) => {
  // Generate temp ID for linking photos
  const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const entry = {
    temp_id: tempId,
    title,
    description: description || null,
    category: category || 'security',
    severity: severity || 'medium',
    site_id: siteId,
    patrol_run_id: patrolRunId || null,
    checkpoint_id: checkpointId || null,
    gps_lat: gpsLat || null,
    gps_lng: gpsLng || null,
    photos: [], // Will be populated with queued photo IDs
    status: QUEUE_STATUS.PENDING,
    created_at: new Date().toISOString(),
    retry_count: 0,
    last_error: null,
    server_id: null // Real ID after sync
  };
  
  // Queue any attached photos
  for (const photo of photos) {
    const photoId = await queuePhoto({
      file: photo.file,
      incidentId: tempId, // Use temp ID, will be updated on sync
      gpsLat,
      gpsLng
    });
    entry.photos.push(photoId);
  }
  
  const id = await db.incidentQueue.add(entry);
  console.log('[OfflineQueue] Incident queued:', id, tempId);
  
  // Try to sync immediately if online
  if (navigator.onLine) {
    syncAll();
  }
  
  return { id, tempId };
};

/**
 * Get all pending items from queue
 */
export const getPendingItems = async () => {
  const photos = await db.photoQueue
    .where('status')
    .anyOf([QUEUE_STATUS.PENDING, QUEUE_STATUS.FAILED, QUEUE_STATUS.RETRYING])
    .toArray();
    
  const incidents = await db.incidentQueue
    .where('status')
    .anyOf([QUEUE_STATUS.PENDING, QUEUE_STATUS.FAILED, QUEUE_STATUS.RETRYING])
    .toArray();
    
  return { photos, incidents, total: photos.length + incidents.length };
};

/**
 * Get queue statistics
 */
export const getQueueStats = async () => {
  const photos = await db.photoQueue.toArray();
  const incidents = await db.incidentQueue.toArray();
  
  const photoStats = {
    total: photos.length,
    pending: photos.filter(p => p.status === QUEUE_STATUS.PENDING).length,
    uploading: photos.filter(p => p.status === QUEUE_STATUS.UPLOADING).length,
    uploaded: photos.filter(p => p.status === QUEUE_STATUS.UPLOADED).length,
    failed: photos.filter(p => p.status === QUEUE_STATUS.FAILED).length
  };
  
  const incidentStats = {
    total: incidents.length,
    pending: incidents.filter(i => i.status === QUEUE_STATUS.PENDING).length,
    uploading: incidents.filter(i => i.status === QUEUE_STATUS.UPLOADING).length,
    uploaded: incidents.filter(i => i.status === QUEUE_STATUS.UPLOADED).length,
    failed: incidents.filter(i => i.status === QUEUE_STATUS.FAILED).length
  };
  
  return {
    photos: photoStats,
    incidents: incidentStats,
    totalPending: photoStats.pending + incidentStats.pending,
    totalFailed: photoStats.failed + incidentStats.failed
  };
};

/**
 * Sync all pending photos
 */
export const syncPhotos = async (api) => {
  if (!navigator.onLine) {
    console.log('[OfflineQueue] Offline - skipping photo sync');
    return { synced: 0, failed: 0 };
  }
  
  const pending = await db.photoQueue
    .where('status')
    .anyOf([QUEUE_STATUS.PENDING, QUEUE_STATUS.FAILED])
    .and(item => item.retry_count < MAX_RETRIES)
    .toArray();
    
  if (pending.length === 0) {
    return { synced: 0, failed: 0 };
  }
  
  console.log(`[OfflineQueue] Syncing ${pending.length} photos...`);
  
  let synced = 0;
  let failed = 0;
  
  for (const item of pending) {
    try {
      // Update status to uploading
      await db.photoQueue.update(item.id, { status: QUEUE_STATUS.UPLOADING });
      
      // Convert base64 back to file
      const file = base64ToFile(item.file_data, item.file_name, item.file_type);
      
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      if (item.caption) formData.append('caption', item.caption);
      if (item.gps_lat) formData.append('gps_lat', item.gps_lat);
      if (item.gps_lng) formData.append('gps_lng', item.gps_lng);
      formData.append('captured_at', item.created_at);
      
      let response;
      
      // If linked to incident, upload to incident endpoint
      if (item.incident_id && !item.incident_id.startsWith('temp_')) {
        response = await api.post(`/incidents/${item.incident_id}/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000
        });
      } else {
        // Upload to general files endpoint
        response = await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000
        });
      }
      
      // Update as uploaded
      await db.photoQueue.update(item.id, {
        status: QUEUE_STATUS.UPLOADED,
        uploaded_url: response.data.url || response.data.file_url,
        synced_at: new Date().toISOString()
      });
      
      synced++;
      console.log(`[OfflineQueue] Photo ${item.id} uploaded successfully`);
      
    } catch (error) {
      console.error(`[OfflineQueue] Photo ${item.id} upload failed:`, error);
      
      const newRetryCount = item.retry_count + 1;
      const newStatus = newRetryCount >= MAX_RETRIES ? QUEUE_STATUS.FAILED : QUEUE_STATUS.PENDING;
      
      await db.photoQueue.update(item.id, {
        status: newStatus,
        retry_count: newRetryCount,
        last_error: error.message || 'Upload failed'
      });
      
      failed++;
    }
  }
  
  return { synced, failed };
};

/**
 * Sync all pending incidents
 */
export const syncIncidents = async (api) => {
  if (!navigator.onLine) {
    console.log('[OfflineQueue] Offline - skipping incident sync');
    return { synced: 0, failed: 0 };
  }
  
  const pending = await db.incidentQueue
    .where('status')
    .anyOf([QUEUE_STATUS.PENDING, QUEUE_STATUS.FAILED])
    .and(item => item.retry_count < MAX_RETRIES)
    .toArray();
    
  if (pending.length === 0) {
    return { synced: 0, failed: 0 };
  }
  
  console.log(`[OfflineQueue] Syncing ${pending.length} incidents...`);
  
  let synced = 0;
  let failed = 0;
  
  for (const item of pending) {
    try {
      // Update status to uploading
      await db.incidentQueue.update(item.id, { status: QUEUE_STATUS.UPLOADING });
      
      // Create incident
      const incidentData = {
        title: item.title,
        description: item.description,
        category: item.category,
        severity: item.severity,
        site_id: item.site_id,
        patrol_run_id: item.patrol_run_id,
        checkpoint_id: item.checkpoint_id,
        occurred_at: item.created_at,
        status: 'open'
      };
      
      if (item.gps_lat && item.gps_lng) {
        incidentData.gps = {
          latitude: item.gps_lat,
          longitude: item.gps_lng
        };
      }
      
      const response = await api.post('/incidents', incidentData, { timeout: 30000 });
      const serverId = response.data.id;
      
      // Update any queued photos to use real incident ID
      if (item.photos && item.photos.length > 0) {
        for (const photoId of item.photos) {
          await db.photoQueue.update(photoId, { incident_id: serverId });
        }
      }
      
      // Update as uploaded
      await db.incidentQueue.update(item.id, {
        status: QUEUE_STATUS.UPLOADED,
        server_id: serverId,
        synced_at: new Date().toISOString()
      });
      
      synced++;
      console.log(`[OfflineQueue] Incident ${item.id} created (server ID: ${serverId})`);
      
    } catch (error) {
      console.error(`[OfflineQueue] Incident ${item.id} creation failed:`, error);
      
      const newRetryCount = item.retry_count + 1;
      const newStatus = newRetryCount >= MAX_RETRIES ? QUEUE_STATUS.FAILED : QUEUE_STATUS.PENDING;
      
      await db.incidentQueue.update(item.id, {
        status: newStatus,
        retry_count: newRetryCount,
        last_error: error.message || 'Creation failed'
      });
      
      failed++;
    }
  }
  
  return { synced, failed };
};

/**
 * Sync all pending items
 */
export const syncAll = async (api) => {
  console.log('[OfflineQueue] Starting full sync...');
  
  // Sync incidents first (to get real IDs for photos)
  const incidentResult = await syncIncidents(api);
  
  // Then sync photos
  const photoResult = await syncPhotos(api);
  
  return {
    incidents: incidentResult,
    photos: photoResult,
    totalSynced: incidentResult.synced + photoResult.synced,
    totalFailed: incidentResult.failed + photoResult.failed
  };
};

/**
 * Retry a specific failed item
 */
export const retryItem = async (type, id) => {
  if (type === 'photo') {
    await db.photoQueue.update(id, {
      status: QUEUE_STATUS.PENDING,
      retry_count: 0
    });
  } else if (type === 'incident') {
    await db.incidentQueue.update(id, {
      status: QUEUE_STATUS.PENDING,
      retry_count: 0
    });
  }
};

/**
 * Delete an item from queue
 */
export const deleteItem = async (type, id) => {
  if (type === 'photo') {
    await db.photoQueue.delete(id);
  } else if (type === 'incident') {
    // Also delete associated photos
    const incident = await db.incidentQueue.get(id);
    if (incident?.photos) {
      for (const photoId of incident.photos) {
        await db.photoQueue.delete(photoId);
      }
    }
    await db.incidentQueue.delete(id);
  }
};

/**
 * Clear all uploaded items (cleanup)
 */
export const clearUploaded = async () => {
  await db.photoQueue.where('status').equals(QUEUE_STATUS.UPLOADED).delete();
  await db.incidentQueue.where('status').equals(QUEUE_STATUS.UPLOADED).delete();
};

/**
 * Clear all items (full reset)
 */
export const clearAll = async () => {
  await db.photoQueue.clear();
  await db.incidentQueue.clear();
  await db.syncQueue.clear();
};

// Helper: Convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

// Helper: Convert base64 to file
const base64ToFile = (base64, fileName, mimeType) => {
  const arr = base64.split(',');
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], fileName, { type: mimeType });
};

export default db;
